/**
 * Tumbler Pin Access Evaluator
 *
 * Shared "combination lock" mechanism for ALL `gate_`-prefixed host patterns
 * (the full-page ACCESS badge reader today, future compact "safe box" widget
 * patterns later). Same 7-pin combination semantics as the tumbler pins
 * configured at the TSX host level (Component_Registry2 /
 * TUMBLER_PIN_DEFINITIONS), but evaluated here against the edge-level pins
 * carried on a single ManifestButton (Directory_Button_Assignments row) —
 * this lets one shared renderer component serve many different target
 * directories/routes, each with its own combination.
 *
 * Mechanism is intentionally decoupled from shape/input: every Gate host
 * calls evaluateTumblerAccess(button, auth) after its own badge/PIN/OTP
 * capture step succeeds, then either routes (handleManifestButtonClick) or
 * shows a denial state — the check itself never changes, only what UI
 * collected the credentials beforehand.
 *
 * Pin semantics (mirrors TUMBLER_PIN_DEFINITIONS in
 * scr_tgc_10378_component_registry_crud.tsx):
 *   pin1_route_guard:    0=open (bypass all other pins), 1=guarded
 *   pin2_jwt_auth:       0=public, 1=JWT required
 *   pin3_org_assoc:      0=none, 1=any association, 2=active, 3=affirmed
 *   pin4_dept_validation:0=none, 1=dept, 2=deptgroup, 3=orgrole, 4=extended
 *   pin5_mgt_hierarchy:  0=all, 1=transactional, 2=supervisor, 3=process,
 *                        4=sysmgt, 5=execmgt, 6=board
 *   pin6_superadmin:     0=user, 1=superadmin
 *   pin7_manage_auth:    0=static, 1=CRUD preauth (advisory only — see below)
 *
 * A pin value of null/undefined (omitted from the manifest when NULL in the
 * DB) means "no additional check for that pin" — always treat absent the
 * same as 0/null.
 *
 * Created: 2026-10-29
 */

import type { ManifestButton } from '../TGC/ComponentTemplates/DirectoryPatterns/manifest-types';
import type { UseAuthResult } from './useAuth';

export type TumblerDenialReason =
    | 'JWT_REQUIRED'
    | 'ORG_ASSOCIATION_REQUIRED'
    | 'DEPT_VALIDATION_FAILED'
    | 'INSUFFICIENT_HIERARCHY'
    | 'SUPERADMIN_REQUIRED';

export interface TumblerAccessResult {
    allowed: boolean;
    /** Which pin (1-7) failed first, if denied */
    failedPin?: number;
    reason?: TumblerDenialReason;
    message?: string;
    /**
     * Pin 7 (manage_auth) is advisory, not a gate: when true, the caller
     * should obtain CRUD-preauth before mounting an edit-capable target,
     * rather than being blocked here outright.
     */
    requiresManageAuth: boolean;
}

/** Pin5 ordinal order — index = required pin value (0 = no requirement) */
const MGT_HIERARCHY_ORDER = [
    'all',
    'transactional',
    'supervisor',
    'process',
    'sysmgt',
    'execmgt',
    'board',
];

function normalize(value: string | undefined | null): string {
    return (value || '').trim().toLowerCase();
}

/** The role assignment matching the user's currently active organization, if any */
function getActiveRoleAssignment(auth: UseAuthResult) {
    if (!auth.activeOrganizationUUID) return null;
    return (
        auth.userRoleAssignments.find(
            (ra) => ra.organization_uuid === auth.activeOrganizationUUID,
        ) || null
    );
}

/**
 * Evaluate a button's 7-pin tumbler combination against the current user's
 * auth context (from useAuth()). Pins are checked in order 1→7; the first
 * failing pin short-circuits the result.
 */
export function evaluateTumblerAccess(
    button: Pick<
        ManifestButton,
        | 'tumbler_pin1_route_guard'
        | 'tumbler_pin2_jwt_auth'
        | 'tumbler_pin3_org_assoc'
        | 'tumbler_pin4_dept_validation'
        | 'tumbler_pin5_mgt_hierarchy'
        | 'tumbler_pin6_superadmin'
        | 'tumbler_pin7_manage_auth'
    >,
    auth: UseAuthResult,
): TumblerAccessResult {
    const requiresManageAuth = (button.tumbler_pin7_manage_auth ?? 0) === 1;

    // Pin 1: master switch — 0/null = open, bypass every other pin
    const routeGuard = button.tumbler_pin1_route_guard ?? 0;
    if (routeGuard === 0) {
        return { allowed: true, requiresManageAuth };
    }

    // Pin 2: JWT auth required
    const jwtRequired = (button.tumbler_pin2_jwt_auth ?? 0) === 1;
    if (jwtRequired && !auth.isAuthenticated) {
        return {
            allowed: false,
            failedPin: 2,
            reason: 'JWT_REQUIRED',
            message: 'Please log in to access this destination.',
            requiresManageAuth,
        };
    }

    const activeRoleAssignment = getActiveRoleAssignment(auth);

    // Pin 3: organization association level
    const orgAssocLevel = button.tumbler_pin3_org_assoc ?? 0;
    if (orgAssocLevel >= 1) {
        const hasAnyAssociation = auth.userOrganizations.length > 0;
        if (!hasAnyAssociation) {
            return {
                allowed: false,
                failedPin: 3,
                reason: 'ORG_ASSOCIATION_REQUIRED',
                message:
                    'You need to be associated with an organization to access this destination.',
                requiresManageAuth,
            };
        }
        // Levels 2 (active) and 3 (affirmed) both require an active role
        // assignment in the user's currently selected organization.
        // NOTE: the Role_Assignment_Status enum does not yet distinguish an
        // "affirmed" state from "active" (2=Active Role Assignment is the
        // strongest confirmed state) — both levels are treated the same
        // here until/unless a dedicated affirmed status is added.
        if (orgAssocLevel >= 2) {
            const isActive = activeRoleAssignment?.role_assignment_status_id === 2;
            if (!isActive) {
                return {
                    allowed: false,
                    failedPin: 3,
                    reason: 'ORG_ASSOCIATION_REQUIRED',
                    message:
                        'Your organization association must be active to access this destination.',
                    requiresManageAuth,
                };
            }
        }
    }

    // Pin 4: department-level validation
    const deptLevel = button.tumbler_pin4_dept_validation ?? 0;
    if (deptLevel >= 1) {
        const failDept =
            (deptLevel === 1 && !activeRoleAssignment?.department) ||
            (deptLevel === 2 && !activeRoleAssignment?.deptgroup) ||
            (deptLevel >= 3 && !activeRoleAssignment?.orgrole_id);
        if (failDept) {
            return {
                allowed: false,
                failedPin: 4,
                reason: 'DEPT_VALIDATION_FAILED',
                message:
                    'Your organization profile is missing information required for this destination.',
                requiresManageAuth,
            };
        }
    }

    // Pin 5: management hierarchy level
    const requiredHierarchyLevel = button.tumbler_pin5_mgt_hierarchy ?? 0;
    if (requiredHierarchyLevel >= 1) {
        const userHierarchyIndex = MGT_HIERARCHY_ORDER.indexOf(
            normalize(activeRoleAssignment?.orgmgthierarchy),
        );
        if (userHierarchyIndex < requiredHierarchyLevel) {
            return {
                allowed: false,
                failedPin: 5,
                reason: 'INSUFFICIENT_HIERARCHY',
                message: `This destination requires management hierarchy level "${MGT_HIERARCHY_ORDER[requiredHierarchyLevel]}" or higher.`,
                requiresManageAuth,
            };
        }
    }

    // Pin 6: superadmin required
    const superAdminRequired = (button.tumbler_pin6_superadmin ?? 0) === 1;
    if (superAdminRequired && !auth.isSuperAdmin) {
        return {
            allowed: false,
            failedPin: 6,
            reason: 'SUPERADMIN_REQUIRED',
            message: 'This destination is restricted to platform super administrators.',
            requiresManageAuth,
        };
    }

    // Pin 7 is advisory only (requiresManageAuth flag), never a gate
    return { allowed: true, requiresManageAuth };
}
